import { useEffect, useRef, useState } from 'react';
import { X, CheckCircle2 } from 'lucide-react';
import OneLineSignupForm from '@/components/auth/OneLineSignupForm';
import { ymGoal } from '@/lib/analytics';
import { useAuth } from '@/lib/auth';
import { seedBirthAfterSignup } from '@/lib/natalProfileSeed';

/**
 * Окно регистрации на закрытой программе (уровень ЛАЙТ).
 *
 * Выделено из components/money/MoneyRegisterModal.jsx без изменений в
 * поведении и без единой правки текстов: «Где твои деньги» и «Нумерология»
 * закрыты от гостя одинаково, и второго такого же окна в проекте быть не
 * должно — иначе тексты и цели Метрики у двух программ разъедутся.
 *
 * Зачем окно: программа уровня ЛАЙТ. У «Денег» расчёт закрыт тарифным гейтом
 * на бэкенде (require_app_access), и гость получал сырое «Требуется
 * авторизация» после того, как ввёл дату, время и город. У «Нумерологии»
 * расчёт идёт в браузере, сервер её не сторожит — гейт стоит здесь, на
 * «Рассчитать». В обоих случаях на этом месте открывается регистрация одной
 * строкой, тем же общим компонентом, что в натале и хораре.
 *
 * Введённое НЕ теряется: данные формы ждут в состоянии страницы, и сразу
 * после регистрации расчёт делается уже под аккаунтом. Человек остаётся на
 * своей странице и видит свой результат.
 *
 * Цели Метрики — существующие, новых не заводим:
 *   signup_click  (place=<place>) — окно показано, то есть человек упёрся;
 *   signup_submit (place=<place>) — форма отправлена (шлёт сама форма);
 *   signup_completed приходит из register() с тем же place.
 *
 * @param {string} place — 'money' | 'num' | 'love' | 'money_periods'. Ключ идёт и
 *   в цели Метрики, и в data-testid (`${place}-register-*`), поэтому у «Денег»
 *   остаются ровно те testid, что были до выделения компонента.
 * @param {string} [title] — заголовок окна. По умолчанию прежний, поэтому
 *   «Деньги» и «Нумерология» не меняются ни на букву. «Любовь и замужество»
 *   передаёт своё «Твой расчёт готов»: там человек уже ввёл данные, и звать
 *   его «получить доступ» поздно — доступ он уже получает.
 * @param {string} [subtitle] — строка под заголовком, если она нужна.
 * @param {string} [footNote] — подпись под кнопкой вместо стандартной
 *   («Женские события», окно «за 199 ₽» — без «Бесплатно, без привязки карты»).
 * @param {string} [submitLabel] — надпись на кнопке. По умолчанию прежняя
 *   «Получить доступ»; блок комментариев передаёт свою.
 */
const DEFAULT_TITLE =
  'Зарегистрируйся в один клик, чтобы получить доступ, и 14 дней ежедневных прогнозов';

export default function CalcRegisterModal({
  place, open, onClose, onRegistered, title, subtitle, submitLabel, footNote, seedForm = null,
}) {
  const { refreshUser } = useAuth() || {};
  // Адрес, на который ушло письмо: пока пуст — в окне форма, появился —
  // короткое «готово», пока страница считает разбор.
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Один показ — одна цель. Флаг в ref, чтобы перерисовка родителя не
  // задваивала событие; сбрасывается на закрытии, поэтому следующее
  // открытие считается снова.
  const sentRef = useRef(false);
  useEffect(() => {
    if (!open) {
      sentRef.current = false;
      setRegisteredEmail('');
      return;
    }
    if (sentRef.current) return;
    sentRef.current = true;
    ymGoal('signup_click', { place });
  }, [open, place]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
        data-testid={`${place}-register-modal`}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100 transition-colors z-10"
          data-testid={`${place}-register-modal-close`}
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>

        <div className="pt-8 pb-3 px-6 text-center">
          <h3
            className="text-lg font-semibold text-slate-900 leading-snug"
            data-testid={`${place}-register-modal-title`}
          >
            {title || DEFAULT_TITLE}
          </h3>
          {subtitle && (
            <p
              className="mt-1.5 text-sm leading-snug text-slate-600"
              data-testid={`${place}-register-modal-subtitle`}
            >
              {subtitle}
            </p>
          )}
        </div>

        {registeredEmail ? (
          <div className="px-6 pb-6" data-testid={`${place}-register-done`}>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-left">
              <p className="text-sm font-semibold text-emerald-900 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Готово. Считаю твой разбор…</span>
              </p>
              <p className="mt-1 text-xs text-emerald-800 leading-snug">
                Письмо с кнопкой входа отправлено на{' '}
                <span className="font-semibold">{registeredEmail}</span>. Пароль
                не нужен — входить будешь по этой ссылке.
              </p>
            </div>
          </div>
        ) : (
          <OneLineSignupForm
            idPrefix={`${place}-register`}
            submitTestId={`${place}-register-btn`}
            submitLabel={submitLabel || 'Получить доступ'}
            {...(footNote ? { footNote } : {})}
            loadingLabel="Открываю…"
            submitGoal={{ name: 'signup_submit', place }}
            registerPlace={place}
            onRegistered={(email) => {
              setRegisteredEmail(email);
              // Данные рождения, с которыми она считала, — сразу в кабинет
              // (только если он пуст: only_if_absent). Дальше их подставляют
              // все программы (14.10.2026). Регистрацию не задерживаем.
              try {
                const form = typeof seedForm === 'function' ? seedForm() : seedForm;
                if (form) {
                  Promise.resolve(seedBirthAfterSignup(form))
                    .then((ok) => { if (ok && refreshUser) refreshUser(); })
                    .catch(() => {});
                }
              } catch (_e) { /* засев — вспомогательный, регистрацию не держит */ }
              if (typeof onRegistered === 'function') onRegistered(email);
            }}
          />
        )}
      </div>
    </div>
  );
}
